// Downscale/compress large photos in-browser so uploads stay well within
// proxy/body-size limits (production ingress often caps at ~1MB).
// Also converts iPhone HEIC/HEIF photos to JPEG (browsers other than Safari
// can't decode HEIC in a <canvas>).
// Adaptively reduces dimensions + quality until the output is under TARGET_BYTES.
// Falls back to the original file on any error.
const TARGET_BYTES = 500 * 1024; // ~0.5MB, safely under strict production ingress caps

const toBlob = (canvas, quality) => new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));

const isHeic = (file) => {
  const t = (file.type || '').toLowerCase();
  const n = (file.name || '').toLowerCase();
  return t.includes('heic') || t.includes('heif') || n.endsWith('.heic') || n.endsWith('.heif');
};

// iPhone HEIC -> JPEG (lazy-load the decoder only when actually needed).
const heicToJpeg = async (file) => {
  try {
    const heic2any = (await import('heic2any')).default;
    const out = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.9 });
    const blob = Array.isArray(out) ? out[0] : out;
    const name = (file.name || 'image').replace(/\.[^.]+$/, '') + '.jpg';
    return new File([blob], name, { type: 'image/jpeg' });
  } catch {
    return file; // decoding failed — let downstream fall back
  }
};

const renderScaled = (file, maxDim) => new Promise((resolve) => {
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onload = async () => {
    URL.revokeObjectURL(url);
    try {
      let { width, height } = img;
      const scale = Math.min(1, maxDim / Math.max(width, height));
      width = Math.round(width * scale);
      height = Math.round(height * scale);

      if (file.size && file.size < TARGET_BYTES && scale === 1) { resolve(file); return; }

      const render = (w, h, q) => {
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        return toBlob(canvas, q);
      };

      let quality = 0.85;
      let blob = await render(width, height, quality);
      let tries = 0;
      while (blob && blob.size > TARGET_BYTES && tries < 6) {
        tries += 1;
        if (quality > 0.5) { quality -= 0.15; }
        else { width = Math.round(width * 0.8); height = Math.round(height * 0.8); }
        blob = await render(width, height, quality);
      }
      if (!blob) { resolve(file); return; }
      const name = (file.name || 'image').replace(/\.[^.]+$/, '') + '.jpg';
      resolve(new File([blob], name, { type: 'image/jpeg' }));
    } catch {
      resolve(file);
    }
  };
  img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
  img.src = url;
});

export const compressImage = async (file, maxDim = 1920) => {
  if (!file) return file;
  let working = file;
  // Accept HEIC even when the browser reports an empty MIME type.
  if (isHeic(file)) {
    working = await heicToJpeg(file);
  } else if (!file.type?.startsWith('image/')) {
    return file;
  }
  return renderScaled(working, maxDim);
};

export default compressImage;
